import { useMutation } from '@tanstack/react-query'
import {
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  RefreshCw,
  TriangleAlert,
  Upload,
} from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'

import {
  Badge,
  Button,
  CheckboxField,
  EmptyState,
  Page,
  PageHeader,
  Select,
  Separator,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { ColourCodesPanel } from '../components/colour-codes-panel.tsx'
import { ImportCategoriesPanel } from '../components/import-categories-dialog.tsx'
import { ImportProgress } from '../components/import-progress.tsx'
import { RecodePanel } from '../components/recode-panel.tsx'
import {
  ApiError,
  applyProductImport,
  previewProductImport,
  type ImportPreview,
  type ImportResult,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Bringing a catalogue over from another system.
 *
 * The design premise is that **the person doing this is frightened**. It is
 * their whole business going into software they do not trust yet, and nine
 * thousand rows is too many to check by hand. So the screen's job is to make
 * the outcome knowable BEFORE it happens, and to make the parts that are
 * genuinely uncertain small enough to face.
 *
 * Three things follow from that:
 *
 *   1. Nothing is written until the numbers have been shown.
 *   2. Problems are grouped by cause, never listed line by line. "150 linhas
 *      com descrição REUTILIZAR" is a decision; 150 rows is a wall.
 *   3. The rows a machine cannot judge are presented as a short, explicit
 *      task — with the old and new description side by side — instead of being
 *      guessed at silently.
 */

const PROBLEM_LABELS: Record<string, string> = {
  'parked-code': 'Código reservado no sistema antigo (REUTILIZAR)',
  'repaired-price': 'Preço corrigido — o sistema antigo gravou com erro',
  'missing-description': 'Linha sem descrição',
  'missing-code': 'Linha sem código',
  'duplicate-code': 'Código repetido na própria planilha',
  'duplicate-description': 'Descrição repetida na própria planilha',
  'unreadable-price': 'Preço que não pôde ser lido',
  'no-price': 'Sem preço de venda',
}

/** Problems that stop a row, as opposed to ones that only get reported. */
const BLOCKING = new Set([
  'parked-code',
  'missing-description',
  'missing-code',
  'duplicate-code',
  'duplicate-description',
])

const Count = ({
  value,
  label,
  detail,
  tone,
}: {
  value: number
  label: string
  detail: string
  tone: 'brand' | 'warning' | 'muted'
}): ReactNode => (
  <div className="flex-1 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4">
    <p
      className={`text-(length:--text-2xl) font-(--weight-semibold) ${
        tone === 'brand'
          ? 'text-(--brand-primary)'
          : tone === 'warning'
            ? 'text-(--warning)'
            : 'text-(--text-secondary)'
      }`}
    >
      {value.toLocaleString('pt-BR')}
    </p>
    <p className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
      {label}
    </p>
    <p className="text-(length:--text-xs) text-(--text-secondary)">{detail}</p>
  </div>
)

export const ImportarPage = (): ReactNode => {
  const { allows } = useAuth()
  const canWrite = allows('catalog.write')

  /*
   * Três operações diferentes, escolhidas antes do arquivo (F165, F181).
   *
   * Trazer o catálogo CRIA produto; atualizar códigos não cria nada — mexe no
   * que já existe, com estoque e histórico embaixo. São irreversíveis de
   * jeitos diferentes, e por isso a escolha vem primeiro, com o que cada uma
   * faz escrito ao lado.
   *
   * A terceira não pede planilha nenhuma, e mora aqui porque é da mesma
   * família: mexer no código de muitas peças de uma vez. Quem procura "onde
   * mudo o código de tudo" procura nesta tela.
   */
  const [mode, setMode] = useState<
    'import' | 'recode' | 'colour-codes' | 'categories'
  >('import')

  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  /**
   * Os produtos novos esperam a contagem (F133). Marcado de início: quem
   * importa uma lista de preços sem estoque vai contar prateleira por
   * prateleira, e o filtro "Aguardando estoque" em Produtos vira a lista do
   * que falta contar.
   */
  const [awaitingStock, setAwaitingStock] = useState(true)

  /** The operator's answer for each ambiguous row, keyed by spreadsheet line. */
  const [answers, setAnswers] = useState<Map<number, 'reused' | 'edited'>>(
    new Map()
  )

  const read = useMutation({
    mutationFn: previewProductImport,
    onSuccess: (data) => {
      setPreview(data)
      setResult(null)
      setError(null)
      setAnswers(new Map())
    },
    onError: (failure: unknown) => {
      setPreview(null)
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'Não foi possível ler a planilha.'
      )
    },
  })

  const apply = useMutation({
    mutationFn: async (chosen: File) =>
      await applyProductImport(chosen, {
        treatAsReused: [...answers]
          .filter(([, answer]) => answer === 'reused')
          .map(([line]) => line),
        treatAsEdited: [...answers]
          .filter(([, answer]) => answer === 'edited')
          .map(([line]) => line),
        priceListId: null,
        awaitingStock,
      }),
    onSuccess: (data) => {
      setResult(data)
      setError(null)
    },
    onError: (failure: unknown) => {
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'A importação não pôde ser concluída.'
      )
    },
  })

  const choose = (chosen: File | null): void => {
    if (chosen === null) return
    setFile(chosen)
    setResult(null)
    read.mutate(chosen)
  }

  const restart = (): void => {
    setFile(null)
    setPreview(null)
    setResult(null)
    setError(null)
    setAnswers(new Map())
    setAwaitingStock(true)
    if (inputRef.current !== null) inputRef.current.value = ''
  }

  const willImport =
    preview === null
      ? 0
      : preview.counts.create +
        preview.counts.update +
        preview.counts.replace +
        [...answers.values()].length

  const unanswered = preview === null ? 0 : preview.counts.review - answers.size

  return (
    <Page>
      <PageHeader
        title="Importar de outro sistema"
        description="Traga o catálogo de outro sistema, ou acerte de uma vez o código de muitas peças. Nada é gravado até você ver o que vai acontecer."
      />

      {!canWrite && (
        <div
          role="status"
          className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4 text-(length:--text-sm) text-(--text-secondary)"
        >
          Você não tem permissão para alterar o catálogo, então esta tela é só
          leitura.
        </div>
      )}

      <section
        role="group"
        aria-label="O que fazer com a planilha"
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        {(
          [
            {
              id: 'import' as const,
              title: 'Trazer o catálogo',
              detail:
                'Casa pelo código do sistema antigo e cria o que ainda não existe aqui, com preço e custo.',
            },
            {
              id: 'recode' as const,
              title: 'Subir a lista inteira',
              detail:
                'Casa pelo nome: quem já existe passa a valer o código, as barras, o custo e o preço da planilha, e quem não existe é criado. Estoque e marca não são tocados.',
            },
            {
              id: 'colour-codes' as const,
              title: 'Acertar os códigos das cores',
              detail:
                'Sem planilha: a primeira cor de cada produto ganha o -1 no código e quem está sem código de barras ganha o interno. Você vê a lista antes.',
            },
            {
              id: 'categories' as const,
              title: 'Subir só as categorias',
              detail:
                'CSV código; categoria; subcategoria. Só grava a categoria, e só em produto sem categoria: preço, código, estoque e nome ficam como estão. Você vê a prévia antes.',
            },
          ] as const
        ).map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={mode === option.id}
            onClick={() => {
              setMode(option.id)
              restart()
            }}
            className={`rounded-(--radius-md) border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary) ${
              mode === option.id
                ? 'border-(--brand-primary) bg-(--surface-raised)'
                : 'border-(--border-subtle) hover:bg-(--surface-hover)'
            }`}
          >
            <p className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
              {option.title}
            </p>
            <p className="text-(length:--text-xs) text-(--text-secondary)">
              {option.detail}
            </p>
          </button>
        ))}
      </section>

      {mode === 'recode' && <RecodePanel canWrite={canWrite} />}
      {mode === 'colour-codes' && <ColourCodesPanel canWrite={canWrite} />}
      {mode === 'categories' && (
        <section
          aria-label="Subir só as categorias"
          className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4"
        >
          <ImportCategoriesPanel canWrite={canWrite} />
        </section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* 1. The file                                                         */}
      {/* ------------------------------------------------------------------ */}
      {mode === 'import' && result === null && (
        <section
          onDragOver={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => {
            setDragging(false)
          }}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            choose(event.dataTransfer.files.item(0))
          }}
          className={`flex flex-col items-center gap-3 rounded-(--radius-md) border-2 border-dashed p-8 text-center transition-colors ${
            dragging
              ? 'border-(--brand-primary) bg-(--surface-raised)'
              : 'border-(--border-subtle)'
          }`}
        >
          <FileSpreadsheet
            aria-hidden="true"
            className="h-8 w-8 text-(--text-muted)"
          />
          <div>
            <p className="text-(length:--text-md) font-(--weight-medium) text-(--text-primary)">
              {file === null
                ? 'Arraste a planilha aqui'
                : `${file.name} · ${(file.size / 1024).toFixed(0)} KB`}
            </p>
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              Arquivo .xlsx exportado do GDOOR: a lista de produtos ou o
              relatório de preços (Descrição, Preço Custo, Preço Venda)
            </p>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            className="sr-only"
            id="import-file"
            onChange={(event) => {
              choose(event.target.files?.item(0) ?? null)
            }}
          />
          <Button
            type="button"
            variant="secondary"
            leadingIcon={<Upload aria-hidden="true" className="h-4 w-4" />}
            onClick={() => inputRef.current?.click()}
          >
            Escolher arquivo
          </Button>
        </section>
      )}

      {mode === 'import' && read.isPending && (
        <div className="flex items-center justify-center gap-3 py-8">
          <Spinner />
          <span className="text-(length:--text-sm) text-(--text-secondary)">
            Lendo a planilha…
          </span>
        </div>
      )}

      {mode === 'import' && error !== null && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) p-4"
        >
          <TriangleAlert
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-(--danger)"
          />
          <p className="text-(length:--text-sm) text-(--danger)">{error}</p>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* 2. What will happen                                                 */}
      {/* ------------------------------------------------------------------ */}
      {mode === 'import' && apply.isPending && (
        <ImportProgress rows={willImport} noun="produtos" />
      )}

      {mode === 'import' && preview !== null && result === null && (
        <>
          <section className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Badge tone="info">{preview.profile.label}</Badge>
              <span className="text-(length:--text-sm) text-(--text-secondary)">
                {preview.rowCount.toLocaleString('pt-BR')} linhas ·{' '}
                {preview.header.join(' · ')}
              </span>
            </div>

            <div className="flex flex-wrap gap-3">
              <Count
                value={preview.counts.create}
                label="Vão entrar"
                detail="produtos novos"
                tone="brand"
              />
              <Count
                value={preview.counts.update}
                label="Vão ser atualizados"
                detail="já existem aqui"
                tone="muted"
              />
              <Count
                value={preview.counts.replace}
                label="Código reaproveitado"
                detail="produto novo, histórico do antigo preservado"
                tone="warning"
              />
              <Count
                value={preview.counts.skip}
                label="Não vão entrar"
                detail="veja os motivos abaixo"
                tone="muted"
              />
            </div>

            {preview.identity === 'description' && (
              <p
                role="note"
                className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4 text-(length:--text-sm) text-(--text-secondary)"
              >
                Esta planilha não tem código, então cada produto é reconhecido
                pela descrição. Os novos ganham código e código de barras daqui,
                prontos para a etiqueta, e enviar a planilha de novo atualiza os
                preços em vez de duplicar.
                {preview.hasCost &&
                  ' O Preço Custo entra como custo de abertura; custo que já veio de compra não é trocado.'}
              </p>
            )}
          </section>

          {preview.problems.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
                O que a planilha traz de estranho
              </h2>
              <TableContainer>
                <Table>
                  <TableBody>
                    {preview.problems.map((entry) => (
                      <TableRow key={entry.problem}>
                        <TableCell className="w-20 text-right font-(--weight-semibold)">
                          {entry.count.toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <p className="text-(--text-primary)">
                            {PROBLEM_LABELS[entry.problem] ?? entry.problem}
                          </p>
                          <p className="text-(length:--text-xs) text-(--text-muted)">
                            {entry.sample.join(' · ')}
                          </p>
                        </TableCell>
                        <TableCell className="w-32 text-right">
                          <Badge
                            tone={
                              BLOCKING.has(entry.problem)
                                ? 'warning'
                                : 'neutral'
                            }
                          >
                            {BLOCKING.has(entry.problem)
                              ? 'não entra'
                              : 'entra assim'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </section>
          )}

          {/* -------------------------------------------------------------- */}
          {/* 3. The rows only a person can judge                             */}
          {/* -------------------------------------------------------------- */}
          {preview.review.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
                {preview.review.length} códigos que preciso que você olhe
              </h2>
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                A descrição mudou, mas não o bastante para eu ter certeza.
                Corrigiram o nome do mesmo produto, ou usaram o código para
                outra coisa? Um código reaproveitado vira produto novo e o
                antigo mantém o histórico dele.
              </p>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell className="w-16">Cód.</TableHeaderCell>
                      <TableHeaderCell>Era</TableHeaderCell>
                      <TableHeaderCell>Virou</TableHeaderCell>
                      <TableHeaderCell className="w-56">
                        O que é?
                      </TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.review.map((row) => (
                      <TableRow key={row.line}>
                        <TableCell className="text-(--text-muted)">
                          {row.externalCode}
                        </TableCell>
                        <TableCell className="text-(--text-secondary)">
                          {row.previousDescription}
                        </TableCell>
                        <TableCell className="text-(--text-primary)">
                          {row.description}
                        </TableCell>
                        <TableCell>
                          <Select
                            aria-label={`Código ${row.externalCode}`}
                            value={answers.get(row.line) ?? ''}
                            disabled={!canWrite}
                            onChange={(event) => {
                              const next = new Map(answers)
                              const value = event.target.value
                              if (value === '') next.delete(row.line)
                              else
                                next.set(row.line, value as 'reused' | 'edited')
                              setAnswers(next)
                            }}
                          >
                            <option value="">Não importar</option>
                            <option value="edited">
                              Mesmo produto, nome corrigido
                            </option>
                            <option value="reused">
                              Outro produto, código reaproveitado
                            </option>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </section>
          )}

          <Separator />

          {preview.counts.create > 0 && (
            <section className="flex flex-col gap-1">
              <CheckboxField
                id="import-awaiting-stock"
                label="Os produtos novos esperam a contagem do estoque"
                checked={awaitingStock}
                disabled={!canWrite}
                onCheckedChange={(checked) => {
                  setAwaitingStock(checked === true)
                }}
              />
              <p className="pl-6 text-(length:--text-xs) text-(--text-secondary)">
                Entram com estoque zero e marcados &ldquo;Aguardando
                estoque&rdquo;: não vendem até alguém informar a quantidade de
                cada um. Em Produtos, o filtro Situação → Aguardando estoque
                mostra o que falta contar.
              </p>
            </section>
          )}

          <section className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-(length:--text-sm) text-(--text-primary)">
                <strong>{willImport.toLocaleString('pt-BR')}</strong> produtos
                serão gravados.
              </p>
              {unanswered > 0 && (
                <p className="text-(length:--text-xs) text-(--text-secondary)">
                  {unanswered.toLocaleString('pt-BR')} códigos sem resposta
                  ficam de fora — você pode importar agora e resolvê-los depois.
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={restart}>
                Trocar arquivo
              </Button>
              <Button
                type="button"
                disabled={!canWrite || apply.isPending || file === null}
                leadingIcon={
                  apply.isPending ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  )
                }
                onClick={() => {
                  if (file !== null) apply.mutate(file)
                }}
              >
                {apply.isPending ? 'Importando…' : 'Importar'}
              </Button>
            </div>
          </section>
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* 4. The receipt                                                      */}
      {/* ------------------------------------------------------------------ */}
      {mode === 'import' && result !== null && (
        <section
          aria-label="Importação concluída"
          className="flex flex-col gap-4 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-6"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2
              aria-hidden="true"
              className="h-5 w-5 text-(--success)"
            />
            <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
              Importação concluída
            </h2>
          </div>

          <div className="flex flex-wrap gap-3">
            <Count
              value={result.created}
              label="Criados"
              detail="produtos novos"
              tone="brand"
            />
            <Count
              value={result.updated}
              label="Atualizados"
              detail="já existiam"
              tone="muted"
            />
            <Count
              value={result.replaced}
              label="Substituídos"
              detail="código reaproveitado"
              tone="warning"
            />
            <Count
              value={result.priced}
              label="Com preço"
              detail="na lista padrão"
              tone="muted"
            />
            {result.costed > 0 && (
              <Count
                value={result.costed}
                label="Com custo"
                detail="custo de abertura"
                tone="muted"
              />
            )}
          </div>

          {result.unanswered > 0 && (
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              {result.unanswered.toLocaleString('pt-BR')} códigos ficaram de
              fora porque não foram respondidos. Envie a planilha de novo para
              resolvê-los — o que já entrou não será duplicado.
            </p>
          )}

          <Button
            type="button"
            variant="secondary"
            leadingIcon={<RefreshCw aria-hidden="true" className="h-4 w-4" />}
            onClick={restart}
          >
            Importar outra planilha
          </Button>
        </section>
      )}

      {mode === 'import' &&
        preview === null &&
        result === null &&
        !read.isPending && (
          <EmptyState
            icon={<FileSpreadsheet aria-hidden="true" className="h-6 w-6" />}
            title="Nenhuma planilha ainda"
            description="A importação cria os produtos e o preço de venda, e o custo quando a planilha traz Preço Custo. O estoque vem depois: pela contagem ou pelos XMLs das notas de entrada."
          />
        )}
    </Page>
  )
}
